import { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ThemeContext, Button, Container, NavItem, NavList, NavDropdown, Header, Loader, ButtonGroup, Icon, Alert, Tooltip, Accordion, AccordionItem, useToast, Modal, Image, FormProvider, Input, FormConsumer, InputGroup, TextArea, Select, Checkbox, Radio, Slider, DateInput, Footer } from 'mouse-user-interface';
import styles from './Preview.module.scss';

function Preview({ home }) {
    const { theme, setTheme } = useContext(ThemeContext);
    const [brandColor, setBrandColor] = useState('');
    const [loaderColor, setLoaderColor] = useState('');
    const [buttonState, setButtonState] = useState('default');
    const [groupButtonState, setGroupButtonState] = useState('default');
    const [formIsSubmitted, setFormIsSubmitted] = useState(false);
    const [showingToast, setShowingToast] = useState(null);
    const [alerts, setAlerts] = useState({ info: true, success: true, warning: true, error: true });
    const [modalOpen, setModalOpen] = useState({ a: false, b: false, c: false, d: false })
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    const allVisible = Object.values(alerts).every(Boolean);
    const { showToast } = useToast();

    const getHandheldCssBreakpoint = () => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-handheld'), 10);
    const isMobile = () => window.innerWidth < getHandheldCssBreakpoint();

    const formatJson = (data) => JSON.stringify(data, null, 2);

    const schema = yup.object({
        email: yup.string().email('Email is invalid').required('Email is required'),
        username: yup.string().min(6, 'Username is too short').required('Username is required'),
        password: yup.string().min(6, 'Password is too short').required('Password is required'),
        confirmPassword: yup.string().oneOf([yup.ref('password')], 'Both passwords must match').required('Please confirm your password'),
        textarea: yup.string().required('Text Area is required'),
        select: yup.string().required('Select is required'),
        checkbox: yup.string().required('Checkbox is required'),
        radio: yup.string().required('Radio is required'),
        slider: yup.number().min(0).max(100).required(),
        dob: yup.date().required('Date is required')
    });

    useEffect(() => {
        const saved = localStorage.getItem('brandColor');
        if (saved) {
            setBrandColor(saved);
            setLoaderColor(saved);
            document.documentElement.style.setProperty('--color-brand', saved);
            return;
        }
        requestAnimationFrame(() => {
            const computed = getComputedStyle(document.documentElement);
            const currentBrand = computed.getPropertyValue('--color-brand').trim();
            if (currentBrand) {
                setBrandColor(currentBrand);
                setLoaderColor(currentBrand);
            }
        });
    }, []);

    useEffect(() => {
        const scrollable = document.querySelector('body');
        if (scrollable) {
            scrollable.scrollTo({
                top: 0,
                left: 0,
                behavior: 'instant'
            });
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    const handleShowToast = (variant, type, message) => {
        setShowingToast(variant);
        showToast({ type, message });
        setTimeout(() => setShowingToast(null), 250);
    };

    const handleDismissAlert = (type) => {
        setAlerts(prev => ({ ...prev, [type]: false }));
    };

    const handleResetAlerts = () => {
        setAlerts({ info: true, success: true, warning: true, error: true });
    };

    const handleBrandColorChange = (e) => {
        const newColor = e.target.value;
        setBrandColor(newColor);
        localStorage.setItem('brandColor', newColor);
        document.documentElement.style.setProperty('--color-brand', newColor);
    };

    const handleLoaderColorChange = (e) => {
        const newColor = e.target.value;
        setLoaderColor(newColor);
    };

    const toggleTheme = () => {
        setTheme(nextTheme);
    };

    const toggleModal = (type, value) => {
        setModalOpen((prev) => ({
            ...prev,
            [type]: value,
        }));
    };

    const toggleButtonState = () => {
        setButtonState(buttonState === 'default' ? 'loading' : buttonState === 'loading' ? 'disabled' : 'default');
    };

    const toggleGroupButtonState = () => {
        setGroupButtonState(groupButtonState === 'default' ? 'loading' : groupButtonState === 'loading' ? 'disabled' : 'default');
    };

    const buttons = [
        { label: 'Primary', variant: 'primary' },
        { label: 'Outline', variant: 'outline' },
        { label: 'Stripped', variant: 'stripped' },
        { label: 'Success', variant: 'success' },
        { label: 'Info', variant: 'info' },
        { label: 'Warning', variant: 'warning' },
        { label: 'Danger', variant: 'danger' },
    ];

    return (
        <main className={`${styles.preview} ${home ? styles.isHome : ''}`}>
            <Container>
                {!home && (
                    <Container
                        padding='none'
                    >
                        <h1 style={{ margin: '.2rem 0' }}>MouseUI Preview</h1>
                        <span style={{ fontWeight: 200 }}>A Lightweight React UI Library</span>
                    </Container>
                )}
                <h3 style={home ? { marginTop: 0 } : {}}>Themes</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    spaceBetween
                >
                    <p>Selected Theme: <span className='bold'>{theme.charAt(0).toUpperCase() + theme.slice(1)}</span></p>
                    <Button
                        onClick={toggleTheme}
                    >
                        Toggle
                    </Button>
                </Container>

                {!home && (
                    <>
                        <h3>Colour Scheme</h3>
                        <Container
                            background='var(--color-surface)'
                            border
                            radius
                            centre
                            spaceBetween
                        >
                            <p>Brand Color: <span className='bold'>{brandColor}</span></p>
                            <input
                                type='color'
                                value={brandColor}
                                onChange={handleBrandColorChange}
                                className={styles['color-input']}
                            />
                        </Container>
                    </>
                )}

                <h3>Buttons</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                >
                    <Container
                        style={{ marginBottom: '20px' }}
                        background='var(--color-surface-alt)'
                        radius
                        centre
                        border
                        spaceBetween
                    >
                        <p>Current State: <span className='bold'>{buttonState.charAt(0).toUpperCase() + buttonState.slice(1)}</span></p>
                        <Button
                            onClick={toggleButtonState}
                        >
                            Toggle
                        </Button>
                    </Container>
                    <ButtonGroup>
                        {buttons.map(({ label, variant }) => (
                            <Button
                                key={label}
                                loading={buttonState === 'loading'}
                                disabled={buttonState === 'disabled'}
                                variant={variant}
                            >
                                {label}
                            </Button>
                        ))}
                        <Button
                            href='/preview'
                            loading={buttonState === 'loading'}
                            disabled={buttonState === 'disabled'}
                            className={styles.preview__link}
                        >
                            Link
                        </Button>
                    </ButtonGroup>
                </Container>

                <h3>Button Groups</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                >
                    <Container
                        style={{ marginBottom: '20px' }}
                        background='var(--color-surface-alt)'
                        radius
                        centre
                        border
                        spaceBetween
                    >
                        <p>Current State: <span className='bold'>{groupButtonState.charAt(0).toUpperCase() + groupButtonState.slice(1)}</span></p>
                        <Button
                            onClick={toggleGroupButtonState}
                        >
                            Toggle
                        </Button>
                    </Container>
                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'stretch'
                        }}
                        gap='2rem'
                        centre
                        responsive
                    >
                        <Container
                            scrollable
                            style={{
                                overflowX: 'auto',
                                width: '100%',
                            }}
                            background='var(--color-surface)'
                            centre
                            column
                            border
                            radius
                        >
                            <p>Horizontal</p>

                            <Container
                                style={{
                                    display: 'inline-flex',
                                    minWidth: '500px'
                                }}
                                gap='1rem'
                            >
                                <ButtonGroup>
                                    {[1, 2, 3, 4].map((number) => (
                                        <Button
                                            key={number}
                                            loading={groupButtonState === 'loading'}
                                            disabled={groupButtonState === 'disabled'}
                                        >
                                            Button {number}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </Container>
                        </Container>
                        <Container
                            background='var(--color-surface)'
                            centre
                            column
                            border
                            radius
                            spaceBetween
                            style={{ width: 'auto' }}
                        >
                            <p>Vertical</p>
                            <ButtonGroup
                                variant='vertical'
                            >
                                <Button
                                    loading={groupButtonState === 'loading'}
                                    disabled={groupButtonState === 'disabled'}
                                >
                                    Button 6
                                </Button>
                                <Button
                                    variant='outline'
                                    loading={groupButtonState === 'loading'}
                                    disabled={groupButtonState === 'disabled'}
                                >
                                    Button 7
                                </Button>
                                <Button
                                    variant='stripped'
                                    loading={groupButtonState === 'loading'}
                                    disabled={groupButtonState === 'disabled'}
                                >
                                    Button 8
                                </Button>
                            </ButtonGroup>
                        </Container>
                    </Container>
                </Container>

                <h3>Headers</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    column
                >
                    <Header
                        heading='MouseUI'
                        className={`${styles['preview__demo-header']} ${theme === 'light' ? styles['preview__demo-header--light'] : ''}`}
                    >
                        <NavList align='right'>
                            <NavItem href='/preview'>Link 1</NavItem>
                            <NavItem href='/preview'>Link 2</NavItem>
                            <NavItem href='/preview'>Link 3</NavItem>
                            <NavItem href='/preview'>Link 4</NavItem>
                        </NavList>
                    </Header>
                    <Header
                        heading={
                            <Icon
                                name='Mouse'
                                size={50}
                                style={{ margin: '0', transform: 'rotateY(180deg)' }}
                            />
                        }
                        className={`${styles['preview__demo-header']} ${theme === 'light' ? styles['preview__demo-header--light'] : ''}`}
                    >
                        <NavList align='left'>
                            <NavItem href='/preview'>Link 1</NavItem>
                            <NavItem href='/preview'>Link 2</NavItem>
                        </NavList>
                        <NavList align='right'>
                            <NavItem href='/preview'>Link 3</NavItem>
                            <NavDropdown label='Dropdown 1'>
                                <NavItem href='/preview'>Link 4</NavItem>
                                <NavItem href='/preview'>Link 5</NavItem>
                                <NavItem href='/preview'>Link 6</NavItem>
                            </NavDropdown>
                            <NavDropdown label='Dropdown 2'>
                                <NavItem href='/preview'>Link 7</NavItem>
                                <NavItem href='/preview'>Link 8</NavItem>
                                <NavItem href='/preview'>Link 9</NavItem>
                            </NavDropdown>
                        </NavList>
                    </Header>
                </Container>

                <h3>Nav Items</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                >
                    <NavList align='left'>
                        <NavItem href='/preview'>Link</NavItem>
                        <NavDropdown label='Dropdown'>
                            <NavItem href='/preview'>Link 1</NavItem>
                            <NavItem href='/preview'>Link 2</NavItem>
                            <NavItem href='/preview'>Link 3</NavItem>
                        </NavDropdown>
                    </NavList>
                </Container>

                <h3>Nav Groups</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    className={styles['preview__nav-group']}
                >
                    <NavList align='left'>
                        <NavItem href='/preview'>Link 1</NavItem>
                        <NavItem href='/preview'>Link 2</NavItem>
                        <NavItem href='/preview'>Link 3</NavItem>
                    </NavList>
                    <NavList align='right'>
                        <NavDropdown label='Dropdown 1'>
                            <NavItem href='/preview'>Link 4</NavItem>
                            <NavItem href='/preview'>Link 5</NavItem>
                            <NavItem href='/preview'>Link 6</NavItem>
                        </NavDropdown>
                        <NavDropdown label='Dropdown 2'>
                            <NavItem href='/preview'>Link 7</NavItem>
                            <NavItem href='/preview'>Link 8</NavItem>
                            <NavItem href='/preview'>Link 9</NavItem>
                        </NavDropdown>
                        <NavDropdown label='Dropdown 3'>
                            <NavItem href='/preview'>Link 10</NavItem>
                            <NavItem href='/preview'>Link 11</NavItem>
                            <NavItem href='/preview'>Link 12</NavItem>
                        </NavDropdown>
                    </NavList>
                </Container>

                <h3>Forms</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    responsive
                >
                    <FormProvider
                        validationSchema={schema}
                        onSubmit={() => {
                            setFormIsSubmitted(true);
                            showToast({ type: 'success', message: 'Form submitted successfully!' });
                        }}
                        showError={false}
                        className={styles.preview__form}
                    >
                        <Container
                            background='var(--color-surface)'
                            border
                            radius
                            responsive
                            column
                            className={styles['preview__form--wrapper']}
                        >
                            <InputGroup direction='column'>
                                <Input name='email' label='Email' type='email' required />
                                <Input name='username' label='Username' type='text' required />
                                <Input name='password' label='Password' type='password' required showToggle />
                                <Input name='confirmPassword' label='Confirm Password' type='password' required showToggle />
                            </InputGroup>
                            <InputGroup direction='row' spaceBetween gap='1.5rem'>
                                <Select
                                    name='select'
                                    label='Select'
                                    required
                                    options={[
                                        { value: '', label: '--- None ---' },
                                        { value: 'option1', label: 'Option 1' },
                                        { value: 'option2', label: 'Option 2' }
                                    ]}
                                />
                                <Radio
                                    name='radio'
                                    label='Radio'
                                    required
                                    options={[
                                        { value: 'option1', label: 'Option 1' },
                                        { value: 'option2', label: 'Option 2' }
                                    ]}
                                />
                            </InputGroup>
                            <TextArea name='textarea' label='Text Area' required />
                            <Slider
                                name='slider'
                                label='Slider'
                                min={0}
                                max={100}
                                step={1}
                                required
                            />
                            <DateInput
                                name='dob'
                                label='Date'
                                required
                            />
                            <InputGroup direction='row'>
                                <Checkbox
                                    name='checkbox'
                                    label='Checkbox'
                                    required
                                />
                            </InputGroup>
                            <FormConsumer>
                                {({ errors, touched }) => {
                                    const hasErrors = Object.keys(errors).some(
                                        (key) => touched[key] && errors[key]
                                    );
                                    return (
                                        <Button type='submit' disabled={hasErrors}>
                                            Submit
                                        </Button>
                                    );
                                }}
                            </FormConsumer>
                        </Container>
                        <Container
                            scrollable
                            style={{
                                overflowX: 'auto',
                                width: '100%',
                                display: 'inline'
                            }}
                            background='var(--color-surface)'
                            centre
                            column
                            border
                            radius
                        >
                            <FormConsumer>
                                {({ values, errors, touched }) => {
                                    return (
                                        <div className={styles['preview__form--output']}>
                                            <h3>Form Debug</h3>
                                            {formIsSubmitted && (
                                                <Alert type='success'>
                                                    Form submitted successfully!
                                                </Alert>
                                            )}
                                            <div>
                                                <span>Touched:</span>
                                                <pre>{formatJson(touched)}</pre>
                                            </div>
                                            <div>
                                                <span>Values:</span>
                                                <pre>{formatJson(values)}</pre>
                                            </div>
                                            <div>
                                                <span>Errors:</span>
                                                <pre>{formatJson(errors)}</pre>
                                            </div>
                                        </div>
                                    );
                                }}
                            </FormConsumer>
                        </Container>
                    </FormProvider>
                </Container>

                <h3>Alerts</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                >
                    <Container>
                        <Container
                            style={{ marginBottom: '20px', justifyContent: 'flex-end' }}
                            centre
                            padding='none'
                        >
                            <Button
                                onClick={handleResetAlerts}
                                disabled={allVisible}
                            >
                                Reset
                            </Button>
                        </Container>
                        {alerts.success && (
                            <Alert type='success' dismissible onClose={() => handleDismissAlert('success')}>
                                This is a success alert!
                            </Alert>
                        )}
                        {alerts.info && (
                            <Alert type='info' dismissible onClose={() => handleDismissAlert('info')}>
                                This is an info alert!
                            </Alert>
                        )}
                        {alerts.warning && (
                            <Alert type='warning' dismissible onClose={() => handleDismissAlert('warning')}>
                                This is a warning alert!
                            </Alert>
                        )}
                        {alerts.error && (
                            <Alert type='error' dismissible onClose={() => handleDismissAlert('error')}>
                                This is an error alert!
                            </Alert>
                        )}
                    </Container>
                </Container>

                <h3>Toasts</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    spaceBetween
                    column
                    className={styles['preview__toast_demo']}
                    style={{ justifyContent: 'flex-start' }}
                >
                    <p>Click to preview a Toast</p>
                    <ButtonGroup>
                        <Button
                            variant='success'
                            loading={showingToast === 'success'}
                            onClick={() => handleShowToast('success', 'success', 'This is a success toast!')}
                        >
                            Success
                        </Button>
                        <Button
                            variant='info'
                            loading={showingToast === 'info'}
                            onClick={() => handleShowToast('info', 'info', 'This is an info toast!')}
                        >
                            Info
                        </Button>
                        <Button
                            variant='warning'
                            loading={showingToast === 'warning'}
                            onClick={() => handleShowToast('warning', 'warning', 'This is a warning toast!')}
                        >
                            Warning
                        </Button>
                        <Button
                            variant='danger'
                            loading={showingToast === 'danger'}
                            onClick={() => handleShowToast('danger', 'error', 'This is an error toast!')}
                        >
                            Error
                        </Button>
                    </ButtonGroup>
                </Container>

                <h3>Tooltips</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    responsive
                    column
                >
                    <p>{isMobile() ? 'Click to preview a Tooltip' : 'Hover to preview a Tooltip'}</p>
                    <Container
                        centre
                        responsive
                        gap='1rem'
                    >
                        <ButtonGroup>
                            <Tooltip content='Tooltip left' position='left'>
                                <Button
                                    variant='outline'
                                >
                                    Left
                                </Button>
                            </Tooltip>
                            <Tooltip content='Tooltip bottom' position='bottom'>
                                <Button
                                    variant='outline'
                                >
                                    Bottom
                                </Button>
                            </Tooltip>
                            <Tooltip content='Tooltip top' position='top'>
                                <Button
                                    variant='outline'
                                >
                                    Top
                                </Button>
                            </Tooltip>
                            <Tooltip content='Tooltip right' position='right'>
                                <Button
                                    variant='outline'
                                >
                                    Right
                                </Button>
                            </Tooltip>
                            <Tooltip content='Tooltip auto' position='auto'>
                                <Button
                                    variant='outline'
                                >
                                    Auto
                                </Button>
                            </Tooltip>
                        </ButtonGroup>
                    </Container>
                </Container>

                <h3>Loaders</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                >
                    <Container
                        style={{ marginBottom: '20px' }}
                        background='var(--color-surface-alt)'
                        radius
                        centre
                        border
                        spaceBetween
                    >
                        <p>Loader Color: <span className='bold'>{loaderColor}</span></p>
                        <input
                            type='color'
                            value={loaderColor}
                            onChange={handleLoaderColorChange}
                            className={styles['color-input']}
                        />
                    </Container>
                    <Container
                        centre
                        style={{ justifyContent: 'flex-start' }}
                    >
                        <Loader color={loaderColor} />
                        <Loader color={loaderColor} variant='horizontal' />
                    </Container>
                </Container>

                <h3>Accordions</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    responsive
                    column
                >
                    <Container
                        centre
                        responsive
                        column
                        gap='1rem'
                        padding='none'
                        style={{ marginBottom: '20px' }}
                    >
                        <p>Single Open</p>
                        <Accordion>
                            <AccordionItem id='accordion-1' title='Header 1'>
                                Example Content 1
                            </AccordionItem>
                            <AccordionItem id='accordion-2' title='Header 2'>
                                Example Content 2
                            </AccordionItem>
                            <AccordionItem id='accordion-3' title='Header 3'>
                                Example Content 3
                            </AccordionItem>
                            <AccordionItem id='accordion-4' title='Header 4'>
                                Example Content 4
                            </AccordionItem>
                        </Accordion>
                    </Container>
                    <Container
                        centre
                        responsive
                        column
                        gap='1rem'
                        padding='none'
                    >
                        <p>Multiple Open</p>
                        <Accordion allowMultiple>
                            <AccordionItem id='accordion-5' title='Header 1'>
                                Example Content 1
                            </AccordionItem>
                            <AccordionItem id='accordion-6' title='Header 2'>
                                Example Content 2
                            </AccordionItem>
                            <AccordionItem id='accordion-7' title='Header 3'>
                                Example Content 3
                            </AccordionItem>
                            <AccordionItem id='accordion-8' title='Header 4'>
                                Example Content 4
                            </AccordionItem>
                        </Accordion>
                    </Container>
                </Container>

                <h3>Modals</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    centre
                    responsive
                    column
                >
                    <p>Click to preview Modal</p>
                    <ButtonGroup>
                        <Button onClick={() => toggleModal('a', true)}>
                            Small
                        </Button>
                        <Button onClick={() => toggleModal('b', true)}>
                            Medium
                        </Button>
                        <Button onClick={() => toggleModal('c', true)}>
                            Large
                        </Button>
                        <Button onClick={() => toggleModal('d', true)}>
                            Footer
                        </Button>
                    </ButtonGroup>
                    <Modal
                        isOpen={modalOpen.a}
                        onClose={() => toggleModal('a', false)}
                        title='Example Modal'
                        size='sm'
                    >
                        <p>Modal content for Example Modal (sm)</p>
                    </Modal>
                    <Modal
                        isOpen={modalOpen.b}
                        onClose={() => toggleModal('b', false)}
                        title='Example Modal'
                        size='md'
                    >
                        <p>Modal content for Example Modal (md)</p>
                    </Modal>
                    <Modal
                        isOpen={modalOpen.c}
                        onClose={() => toggleModal('c', false)}
                        title='Example Modal'
                        size='lg'
                    >
                        <p>Modal content for Example Modal (lg)</p>
                    </Modal>
                    <Modal
                        isOpen={modalOpen.d}
                        onClose={() => toggleModal('d', false)}
                        title='Example Modal'
                        size='lg'
                        footer={
                            <Button onClick={() => toggleModal('d', false)}>
                                Close
                            </Button>
                        }
                    >
                        <p>Modal content for Example Modal with Footer</p>
                    </Modal>
                </Container>

                <h3>Images</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    responsive
                >
                    <Image
                        src='/images/pexels-photo-126271.jpeg'
                        alt='Example Image 1'
                        width={300}
                        height={200}
                        fit='cover'
                        caption='Default'
                        fallbackSrc='/images/noAccess.svg'
                    />
                    <Image
                        src='/images/pexels-photo-128234.jpeg'
                        alt='Example Image 2'
                        width={300}
                        height={200}
                        fit='cover'
                        caption='Bordered & rounded'
                        rounded
                        shadow
                        border
                        fallbackSrc='/images/noAccess.svg'
                    />
                    <Image
                        src='./images/doesntexist.jpeg'
                        alt='Example Image 3'
                        width={200}
                        height={200}
                        fit='cover'
                        caption='Fallback image'
                        fallbackSrc='/images/noAccess.svg'
                    />
                </Container>

                <h3>Footers</h3>
                <Container
                    background='var(--color-surface)'
                    border
                    radius
                    responsive
                >
                    <Footer
                        columns={3}
                        innerFooterContent={
                            <>
                                &copy; {new Date().getFullYear()} &#8226; Company Name
                            </>
                        }
                    >
                        <Container
                            column
                            gap='.2rem'
                        >
                            <h3>Heading</h3>
                            <Button variant='stripped' href='/preview'>
                                Link 1
                            </Button>
                            <Button variant='stripped' href='/preview'>
                                Link 2
                            </Button>
                            <Button variant='stripped' href='/preview'>
                                Link 3
                            </Button>
                        </Container>
                        <Container
                            column
                            gap='.2rem'
                        >
                            <h3>Heading</h3>
                            <Button variant='stripped' href='/preview'>
                                Link 4
                            </Button>
                            <Button variant='stripped' href='/preview'>
                                Link 5
                            </Button>
                            <Button variant='stripped' href='/preview'>
                                Link 6
                            </Button>
                        </Container>
                        <Container
                            column
                            gap='.2rem'
                        >
                            <h3>Heading</h3>
                            <Button variant='stripped' href='/preview'>
                                Link 7
                            </Button>
                            <Button variant='stripped' href='/preview'>
                                Link 8
                            </Button>
                            <Button variant='stripped' href='/preview'>
                                Link 9
                            </Button>
                        </Container>
                    </Footer>
                </Container>
            </Container>
        </main>
    );
}

export default Preview;